import { UserSpeakerListAPIQueryParams } from '@goldcast/api/content';
import { isString } from 'radash';
import {
  speakerTextColors,
  speakerBgColors,
  speakerLightBgColors,
  speakerHoverBgColors
} from '../constants/speakerColors';
import { getRequiredEnvValue } from '../env';
import { Pronouns, SpeakerWithDetails } from '../types/speaker-types';
import { TranscriptReturn } from '../types/transcript-types';
import { mergeWindows, subtractRanges } from './rangeUtils';
import { getAlignedRecordings } from './alignedRecordings';
import { isEntitySharedVideo } from './activityUtils';
import { ContentMetadataStoreFactoryType } from '../stores/contentMetadataStoreFactory';

export function getSpeakerImage(image?: string) {
  if (!image) {
    return '';
  }
  if (image.startsWith('http')) {
    return image;
  }
  return getRequiredEnvValue('CLOUDFRONT_STATIC_ASSET_URL') + image;
}
export const getSpeakersPayloadParams = (contentId: string, isNotUpload: boolean): UserSpeakerListAPIQueryParams => {
  if (isNotUpload) {
    return { broadcast: contentId };
  }
  return { upload: contentId };
};
export const getSpeakerColorAttributes = (index: number) => {
  return {
    color: speakerTextColors[index % speakerTextColors.length],
    bgColor: speakerBgColors[index % speakerBgColors.length],
    lightBgColor: speakerLightBgColors[index % speakerLightBgColors.length],
    hoverBgColor: speakerHoverBgColors[index % speakerHoverBgColors.length]
  };
};
export const formSpeakerDetails = (
  sessionSpeakers: Record<string, SpeakerWithDetails>
): Record<string, SpeakerWithDetails> => {
  const speakerIds = Object.keys(sessionSpeakers);
  const speakers: Record<string, SpeakerWithDetails> = {};

  speakerIds.forEach((id, index) => {
    const speaker = sessionSpeakers[id];
    speakers[id] = {
      ...speaker,
      key: id,
      id: speaker?.id ?? index,
      profile_picture_url: getSpeakerImage(speaker?.profile_picture_url),
      speakingSlots: {},
      ...getSpeakerColorAttributes(index)
    };
  });

  return speakers;
};
export const populateSpeakersWithSpeakingSlots = ({
  speakers,
  transcriptResult,
  broadcastId,
  contentMetadataStoreInstance
}: {
  speakers: Record<string, SpeakerWithDetails>;
  transcriptResult: TranscriptReturn;
  broadcastId: string;
  contentMetadataStoreInstance: ContentMetadataStoreFactoryType;
}) => {
  const fullTranscript = transcriptResult.fullTranscript;
  let start: number | null = null;
  let end = 0;
  fullTranscript.forEach((word, index) => {
    start = start ?? word.start_time;
    end = word.end_time ?? end;
    if (word.speaker_label !== fullTranscript[index + 1]?.speaker_label) {
      // Create speaker if it doesn't exist
      if (!speakers[word.speaker_label]) {
        const speakerIndex = Object.keys(speakers).length;
        speakers[word.speaker_label] = {
          key: word.speaker_label,
          id: speakerIndex.toString(),
          company: '',
          first_name: 'Speaker ' + speakerIndex,
          last_name: '',
          title: '',
          pronouns: Pronouns.THEY,
          profile_picture_url: '',
          speakingSlots: {},
          parent_id: null,
          ...getSpeakerColorAttributes(speakerIndex)
        };
      }
      speakers[word.speaker_label].speakingSlots[start ?? '0'] = end;
      start = null;
    }
  });
  updateSpeakingSlotsWithSharedVideo({ speakers, broadcastId, contentMetadataStoreInstance });
  return { speakers, end };
};
const updateSpeakingSlotsWithSharedVideo = ({
  speakers,
  broadcastId,
  contentMetadataStoreInstance
}: {
  speakers: Record<string, SpeakerWithDetails>;
  broadcastId: string;
  contentMetadataStoreInstance: ContentMetadataStoreFactoryType;
}) => {
  const alignedRecordings = getAlignedRecordings({
    broadcastId,
    contentMetadataStoreInstance
  });
  const sharedVideos = alignedRecordings.filter(recording => isEntitySharedVideo(recording.entity));
  if (sharedVideos.length === 0) {
    return speakers;
  }
  const sharedVideoSpeakers = new Set(
    sharedVideos
      // TODO: @@HiteshChoudhary0098 this needs to be revisited. What backend stopped sending types for transcript mapping.
      // @ts-ignore
      .map(recording => recording.transcript_mapping?.speaker_labels?.[0])
      .filter(speakerKey => isString(speakerKey))
  );

  const stageTimeSpansForSharedVideo = sharedVideos
    .map(
      recording => recording.stage_time_spans?.map(span => [span.start_time, span.end_time] as [number, number]) ?? []
    )
    .flat();

  const mergedSortedTimeSpansForSharedVideo = mergeWindows(stageTimeSpansForSharedVideo);

  Object.keys(speakers).forEach(speakerKey => {
    if (sharedVideoSpeakers.has(speakerKey)) {
      return;
    }
    const { speakingSlots } = speakers[speakerKey];
    const mergedAndSortedSpeakingSlotsArray = mergeWindows(
      Object.entries(speakingSlots).map(([start, end]) => [+start, end])
    );

    const updatedSpeakingSlots = subtractRanges(mergedAndSortedSpeakingSlotsArray, mergedSortedTimeSpansForSharedVideo);
    speakers[speakerKey].speakingSlots = Object.fromEntries(updatedSpeakingSlots);
  });

  return speakers;
};
export const getVideoDetailsFromSpeaker = (speaker: SpeakerWithDetails) => {
  return {
    videoId: speaker.key,
    name: `Video ${speaker.key}`
  };
};
export function getSpeakerNameAndTitle(speaker): { name: string; title: string } {
  return {
    title: getFormattedTitle(speaker),
    name: `${speaker.first_name} ${speaker.last_name}`
  };
}

export function getFormattedTitle(speaker: SpeakerWithDetails): string {
  const atContent = !!speaker.title && !!speaker.company ? ' at' : '';
  return `${speaker.title || ''}${atContent} ${speaker.company || ''}`.trim();
}
